/* Tiny Walls cart drawer extras (sections/tw-cart-drawer.liquid).
   Sleek's assets/cart.js owns the drawer itself; this adds the free-decal picker, the add-on switch,
   and keeps the drawer in sync when something other than the theme adds to the cart. */
(() => {
  const drawer = document.querySelector('cart-drawer.tw-cart');
  if (!drawer || !window.FoxTheme || !FoxTheme.pubsub) return;

  const panel = drawer.querySelector('[data-tw-offer]');
  const DISMISSED = 'tw-offer-dismissed';
  const wasDismissed = () => {
    try {
      return sessionStorage.getItem(DISMISSED) === '1';
    } catch (error) {
      return false;
    }
  };
  const rememberDismissed = () => {
    try {
      sessionStorage.setItem(DISMISSED, '1');
    } catch (error) {}
  };

  // The shop's money format wraps amounts in a currency-app <span>; the picker only needs the text.
  const money = (cents) =>
    FoxTheme.Currency.formatMoney(cents, FoxTheme.settings.moneyFormat)
      .replace(/<[^>]*>/g, '')
      .trim();
  const shortMoney = (cents) => money(cents).replace(/[.,]00$/, '');
  const escapeHtml = (text) => text.replace(/&/g, '&amp;').replace(/</g, '&lt;');

  const sectionsToRender = () => {
    const sections = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections } })
    );
    return sections;
  };

  const publishCart = (cart) => FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart });

  const fetchCart = () =>
    fetch(`${FoxTheme.routes.cart_url}.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' }).then(
      (response) => response.json()
    );

  /* ---- Refresh the drawer from the server (used when another script changed the cart) ---- */
  let refreshTimer;
  let refreshOpens = false;
  const refreshCart = (open = false) => {
    refreshOpens = refreshOpens || open;
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(async () => {
      const shouldOpen = refreshOpens;
      refreshOpens = false;
      try {
        const [cart, sections] = await Promise.all([
          fetchCart(),
          fetch(`${window.location.pathname}?sections=${sectionsToRender().join(',')}`).then((response) =>
            response.json()
          ),
        ]);
        cart.sections = sections;
        publishCart(cart);
        if (shouldOpen) drawer.show();
      } catch (error) {
        console.error('Cart refresh failed', error);
      }
    }, 60);
  };

  /* ---- Add a variant and re-render, the same way the theme's product form does ---- */
  const addToCart = async (variantId, button) => {
    if (button) {
      button.classList.add('btn--loading');
      button.setAttribute('aria-disabled', 'true');
    }
    try {
      const response = await fetch(`${FoxTheme.routes.cart_add_url}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: Number(variantId), quantity: 1 }],
          sections: sectionsToRender(),
          sections_url: window.location.pathname,
        }),
      });
      const added = await response.json();
      if (!response.ok) throw new Error(added.description || added.message || 'Could not add to cart');
      const cart = await fetchCart();
      cart.sections = added.sections;
      publishCart(cart);
      return true;
    } catch (error) {
      alert(error.message);
      return false;
    } finally {
      if (button) {
        button.classList.remove('btn--loading');
        button.removeAttribute('aria-disabled');
      }
    }
  };

  /* ---- Free-decal picker ---- */
  const readOffer = () => {
    const strip = drawer.querySelector('[data-tw-offer-state]');
    return {
      state: strip ? strip.dataset.twOfferState : 'none',
      ref: strip ? Number(strip.dataset.twOfferRef) : 0,
    };
  };

  let offer = readOffer();
  let activeTab = null;
  const tabCache = new Map();

  const renderPick = (pick) => {
    const control = pick.querySelector('[data-tw-pick-variant]');
    const isSelect = control.tagName === 'SELECT';
    const options = isSelect ? [...control.options].filter((option) => !option.disabled) : [];

    // Until the shopper picks a size, default to the biggest one that is still free.
    if (isSelect && pick.dataset.chosen !== '1' && options.length) {
      const price = (option) => Number(option.dataset.price);
      const free = options.filter((option) => price(option) <= offer.ref);
      const best = free.length
        ? free.reduce((a, b) => (price(b) >= price(a) ? b : a))
        : options.reduce((a, b) => (price(b) < price(a) ? b : a));
      control.value = best.value;
    }

    const selected = isSelect ? control.selectedOptions[0] : control;
    const price = Number(selected.dataset.price);
    const priceEl = pick.querySelector('[data-tw-pick-price]');
    const labelEl = pick.querySelector('[data-tw-pick-label]');

    pick.dataset.free = price <= offer.ref ? '1' : '0';
    if (price <= offer.ref) {
      priceEl.innerHTML = `<s>${shortMoney(price)}</s><span class="tw-free">FREE</span>`;
      labelEl.textContent = 'Add free';
    } else {
      priceEl.innerHTML = `<strong>+${shortMoney(price - offer.ref)}</strong><small>Your ${shortMoney(
        offer.ref
      )} decal becomes the free one</small>`;
      labelEl.textContent = 'Add';
    }
  };

  const renderPanel = () => {
    if (!panel) return;
    panel.querySelector('[data-tw-offer-sub]').innerHTML = escapeHtml(panel.dataset.sub).replace(
      '[amount]',
      `<strong>${shortMoney(offer.ref)}</strong>`
    );
    panel.querySelectorAll('[data-tw-pick]').forEach(renderPick);
  };

  const selectTab = async (tab) => {
    if (!tab) return;
    activeTab = tab;
    panel.querySelectorAll('[data-tw-offer-tab]').forEach((button) => {
      button.setAttribute('aria-selected', button === tab ? 'true' : 'false');
    });
    const list = panel.querySelector('[data-tw-offer-list]');
    const url = tab.dataset.twOfferTab;

    if (!tabCache.has(url)) {
      list.innerHTML = '<p class="tw-offer__loading">Loading decals…</p>';
      try {
        const html = await fetch(url).then((response) => response.text());
        const products = new DOMParser().parseFromString(html, 'text/html').querySelector('.tw-offer-products');
        tabCache.set(url, products ? products.innerHTML : '');
      } catch (error) {
        tabCache.set(url, '');
      }
    }
    if (activeTab !== tab) return;
    list.innerHTML = tabCache.get(url) || '<p class="tw-offer__loading">Could not load this range. Try another tab.</p>';
    list.scrollTop = 0;
    renderPanel();
    // Free picks first, keeping the collection's own order within each group.
    [...list.querySelectorAll('[data-tw-pick]')]
      .sort((a, b) => Number(b.dataset.free) - Number(a.dataset.free))
      .forEach((pick) => list.appendChild(pick));
  };

  const openPanel = () => {
    if (!panel || offer.state !== 'unlocked' || !drawer.open) return;
    renderPanel();
    if (!activeTab) selectTab(panel.querySelector('[data-tw-offer-tab]'));
    panel.removeAttribute('inert');
    panel.setAttribute('aria-hidden', 'false');
    drawer.setAttribute('data-tw-offer-active', '');
  };

  const closePanel = () => {
    if (!panel) return;
    drawer.removeAttribute('data-tw-offer-active');
    panel.setAttribute('aria-hidden', 'true');
    panel.setAttribute('inert', '');
  };

  let openTimer;
  const openPanelSoon = () => {
    clearTimeout(openTimer);
    openTimer = setTimeout(openPanel, 450);
  };

  /* ---- Countdown: starts when the first best seller lands, survives reloads, clears when none left ---- */
  const TIMER_KEY = 'tw-offer-timer';
  const store = {
    get: () => { try { return Number(localStorage.getItem(TIMER_KEY)) || 0; } catch (e) { return 0; } },
    set: (value) => { try { localStorage.setItem(TIMER_KEY, String(value)); } catch (e) {} },
    clear: () => { try { localStorage.removeItem(TIMER_KEY); } catch (e) {} },
  };
  let tick;
  const syncTimer = () => {
    clearInterval(tick);
    const el = drawer.querySelector('[data-tw-timer]');
    if (!el) {
      if (offer.state === 'none') store.clear();
      return;
    }
    const minutes = Number(el.dataset.twTimerMinutes) || 20;
    let end = store.get();
    if (!end || end > Date.now() + minutes * 60000) {
      end = Date.now() + minutes * 60000;
      store.set(end);
    }
    const label = el.querySelector('[data-tw-timer-label]');
    const clock = el.querySelector('[data-tw-timer-clock]');
    // Only touch the DOM when something changed: every write here is a mutation the drawer observer sees.
    const put = (node, text) => { if (node.textContent !== text) node.textContent = text; };
    const render = () => {
      const left = Math.max(0, end - Date.now());
      if (left <= 0) {
        clearInterval(tick);
        el.classList.add('is-over');
        el.classList.remove('is-urgent');
        put(label, el.dataset.twTimerDone);
        put(clock, '');
        return;
      }
      const m = Math.floor(left / 60000);
      const s = Math.floor((left % 60000) / 1000);
      put(label, el.dataset.twTimerText);
      put(clock, `${m}:${String(s).padStart(2, '0')}`);
      el.classList.toggle('is-urgent', left < 5 * 60000);
    };
    el.hidden = false;
    render();
    tick = setInterval(render, 1000);
  };
  syncTimer();

  // Runs after every re-render of the drawer. The picker pops out when the first qualifying decal lands.
  const syncOffer = () => {
    const previous = offer.state;
    offer = readOffer();
    syncTimer();
    if (offer.state !== 'unlocked') {
      closePanel();
      return;
    }
    if (drawer.hasAttribute('data-tw-offer-active')) {
      renderPanel();
    } else if (previous === 'none' && !wasDismissed()) {
      drawer.open ? openPanelSoon() : (drawer.dataset.twOfferPending = '1');
    }
  };

  new MutationObserver((records) => {
    // The countdown rewrites its own text every second; that is not a cart re-render.
    const timer = drawer.querySelector('[data-tw-timer]');
    if (timer && records.every((record) => timer.contains(record.target))) return;
    syncOffer();
  }).observe(drawer.querySelector('.drawer__content'), { childList: true, subtree: true });

  drawer.addEventListener('toggle', () => {
    if (!drawer.open) {
      closePanel();
    } else if (drawer.dataset.twOfferPending) {
      delete drawer.dataset.twOfferPending;
      openPanelSoon();
    }
  });

  drawer.addEventListener('click', async (event) => {
    if (event.target.closest('[data-tw-offer-open]')) {
      openPanel();
    } else if (event.target.closest('[data-tw-offer-dismiss]')) {
      rememberDismissed();
      closePanel();
    } else if (event.target.closest('[data-tw-offer-tab]')) {
      selectTab(event.target.closest('[data-tw-offer-tab]'));
    } else if (event.target.closest('[data-tw-pick-add]')) {
      const button = event.target.closest('[data-tw-pick-add]');
      if (button.hasAttribute('aria-disabled')) return;
      const control = button.closest('[data-tw-pick]').querySelector('[data-tw-pick-variant]');
      if (await addToCart(control.value, button)) closePanel();
    }
  });

  drawer.addEventListener('change', async (event) => {
    if (event.target.matches('[data-tw-pick-variant]')) {
      const pick = event.target.closest('[data-tw-pick]');
      pick.dataset.chosen = '1';
      renderPick(pick);
    } else if (event.target.matches('[data-tw-addon]')) {
      event.target.disabled = true;
      if (!(await addToCart(event.target.dataset.twAddon))) {
        event.target.checked = false;
        event.target.disabled = false;
      }
    }
  });

  // Links such as the abandoned-cart email land with ?free-decal in the URL: open the cart,
  // and the picker too if a free decal is still unclaimed.
  if (new URLSearchParams(window.location.search).has('free-decal')) {
    drawer.dataset.twOfferPending = '1';
    drawer.show();
  }

  /* ---- Keep the drawer in sync with cart changes made outside the theme ----
     The theme's own requests ask for re-rendered sections; anything that doesn't (custom
     add-to-cart buttons, page-builder blocks, apps) gets a refresh, and an add opens the drawer. */
  const CART_CHANGE = /\/cart\/(add|change|update|clear)(\.js|\.json)?(\?|$)/;
  const asksForSections = (body) => {
    if (typeof body === 'string') return body.includes('sections');
    if (body instanceof FormData || body instanceof URLSearchParams) return body.has('sections');
    return false;
  };

  const nativeFetch = window.fetch;
  window.fetch = function (input, init) {
    const result = nativeFetch.apply(this, arguments);
    const match = CART_CHANGE.exec(typeof input === 'string' ? input : (input && input.url) || '');
    if (match && !asksForSections(init && init.body)) {
      result.then((response) => response.ok && refreshCart(match[1] === 'add')).catch(() => {});
    }
    return result;
  };

  const nativeOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    const match = CART_CHANGE.exec(String(url));
    if (match) {
      this.addEventListener('load', () => {
        if (this.status >= 200 && this.status < 300) refreshCart(match[1] === 'add');
      });
    }
    return nativeOpen.apply(this, arguments);
  };

  // The custom Tiny Walls sections (PDP picker, collection quick-add, calculators) call these two
  // Unicorn helpers after adding to cart. Keep the names working now that Unicorn is gone.
  if (typeof window.unicornRefreshCart !== 'function') window.unicornRefreshCart = () => refreshCart();
  if (typeof window.unicornOpenCart !== 'function') window.unicornOpenCart = () => drawer.show();
})();
